import React, { useEffect, useMemo, useState } from "react";
import { X, Download, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";

/**
 * DocumentPreviewModal — Phase 10 Fase A
 *
 * Layout a 3 zone (flex column):
 *  - Header dark ~56px con titolo + azioni (Scarica, Chiudi)
 *  - Body flex:1 con visualizzatore full-area (iframe PDF via Blob URL, img contain, fallback)
 *  - Nessun footer
 *
 * Dimensioni:
 *  - Desktop (>=768px): 95vw × 90vh
 *  - Mobile (<768px): fullscreen 100vw × 100vh, no border-radius
 *
 * Blob URL pattern (invariato): fetch cross-origin con credentials:'include',
 * conversione a Blob, URL.createObjectURL(). Cleanup revoke su unmount/swap.
 * Il PDF viewer nativo del browser (con toolbar zoom/download/pagine) è
 * conservato integralmente perché non manipoliamo l'src dell'iframe.
 */
export default function DocumentPreviewModal({ item, downloadUrl, onDownload, onClose }) {
    const contentType = (item?.content_type || "").toLowerCase();
    const kind = useMemo(() => {
        if (contentType === "application/pdf") return "pdf";
        if (contentType.startsWith("image/")) return "image";
        return "other";
    }, [contentType]);

    const [blobUrl, setBlobUrl] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(kind === "pdf" || kind === "image");

    // ESC per chiudere
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Fetch blob per PDF/immagini. credentials:'include' garantisce l'invio
    // del cookie JWT httpOnly. CORS backend già configurato (Allow-Credentials).
    useEffect(() => {
        if (kind !== "pdf" && kind !== "image") return;
        let cancelled = false;
        let currentBlobUrl = null;
        setLoading(true);
        setLoadError(null);
        setBlobUrl(null);

        (async () => {
            try {
                const res = await fetch(`${downloadUrl}?inline=1`, { credentials: "include" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                const typedBlob = kind === "pdf" && blob.type !== "application/pdf"
                    ? new Blob([blob], { type: "application/pdf" })
                    : blob;
                currentBlobUrl = URL.createObjectURL(typedBlob);
                setBlobUrl(currentBlobUrl);
                setLoading(false);
            } catch (err) {
                if (cancelled) return;
                setLoadError(err?.message || "Errore caricamento");
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
        };
    }, [downloadUrl, kind]);

    const iconFor = () => {
        if (kind === "pdf") return <FileText size={18} strokeWidth={1.75} />;
        if (kind === "image") return <ImageIcon size={18} strokeWidth={1.75} />;
        return <FileIcon size={18} strokeWidth={1.75} />;
    };

    return (
        <div className="doc-preview-overlay" onClick={onClose} data-testid="doc-preview-backdrop">
            <div
                className="doc-preview-modal"
                onClick={(e) => e.stopPropagation()}
                data-testid="doc-preview-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Anteprima ${item.name}`}
            >
                {/* HEADER */}
                <div className="doc-preview-head">
                    <div className="doc-preview-head__title-group">
                        <span aria-hidden="true" style={{ color: "#EFEFEC", flexShrink: 0 }}>{iconFor()}</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <h2 className="doc-preview-head__title" data-testid="doc-preview-title">
                                {item.name}
                            </h2>
                            {item.original_filename && (
                                <div className="doc-preview-head__filename">{item.original_filename}</div>
                            )}
                        </div>
                    </div>
                    <div className="doc-preview-head__actions">
                        <button
                            type="button"
                            className="doc-preview-head__btn"
                            onClick={onDownload}
                            data-testid="doc-preview-download"
                            aria-label="Scarica documento"
                        >
                            <Download size={13} strokeWidth={2} />
                            <span className="doc-preview-btn-label">Scarica</span>
                        </button>
                        <button
                            type="button"
                            className="doc-preview-head__icon-btn"
                            onClick={onClose}
                            aria-label="Chiudi anteprima"
                            data-testid="doc-preview-close"
                        >
                            <X size={18} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                {/* BODY */}
                <div className="doc-preview-body" data-testid="doc-preview-body">
                    {(loading && (kind === "pdf" || kind === "image")) && (
                        <div className="doc-preview-body__loading" data-testid={`doc-preview-${kind}-loading`}>
                            <span className="doc-preview-spinner" aria-hidden="true" />
                            <span>Caricamento anteprima…</span>
                        </div>
                    )}

                    {kind === "pdf" && !loading && loadError && (
                        <div className="doc-preview-body__fallback" data-testid="doc-preview-pdf-error">
                            <p>Impossibile caricare l&apos;anteprima ({loadError}).</p>
                            <button className="doc-preview-head__btn" onClick={onDownload}>
                                <Download size={13} strokeWidth={2} />
                                <span>Scarica</span>
                            </button>
                        </div>
                    )}
                    {kind === "pdf" && blobUrl && !loadError && (
                        <iframe
                            title={`Anteprima ${item.name}`}
                            src={blobUrl}
                            className="doc-preview-body__iframe"
                            data-testid="doc-preview-pdf-iframe"
                        />
                    )}

                    {kind === "image" && !loading && loadError && (
                        <div className="doc-preview-body__fallback" data-testid="doc-preview-image-error">
                            <p>Impossibile caricare l&apos;anteprima.</p>
                            <button className="doc-preview-head__btn" onClick={onDownload}>
                                <Download size={13} strokeWidth={2} />
                                <span>Scarica</span>
                            </button>
                        </div>
                    )}
                    {kind === "image" && blobUrl && !loadError && (
                        <img
                            src={blobUrl}
                            alt={item.name}
                            className="doc-preview-body__img"
                            data-testid="doc-preview-image"
                        />
                    )}

                    {kind === "other" && (
                        <div className="doc-preview-body__fallback" data-testid="doc-preview-fallback">
                            <FileIcon size={56} strokeWidth={1.25} />
                            <div style={{ maxWidth: 460 }}>
                                <div style={{ color: "#EFEFEC", fontSize: 18, marginBottom: 8, fontWeight: 500 }}>
                                    Anteprima non disponibile per questo tipo di file
                                </div>
                                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                                    Il formato <span style={{ color: "#EFEFEC" }}>{contentType || "sconosciuto"}</span> non è visualizzabile in-app.
                                    Scarica il file per aprirlo con l&apos;applicazione dedicata.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onDownload}
                                data-testid="doc-preview-fallback-download"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 10,
                                    padding: "14px 28px",
                                    background: "#E5FF00", color: "#181B20",
                                    border: "none", borderRadius: 3,
                                    fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                    fontSize: 13, fontWeight: 600,
                                    letterSpacing: 1.2, textTransform: "uppercase",
                                    cursor: "pointer",
                                    boxShadow: "0 0 0 1px rgba(229,255,0,0.4), 0 8px 20px rgba(229,255,0,0.15)",
                                    transition: "transform 120ms ease, box-shadow 120ms ease",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 0 0 1px rgba(229,255,0,0.6), 0 10px 24px rgba(229,255,0,0.25)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 0 1px rgba(229,255,0,0.4), 0 8px 20px rgba(229,255,0,0.15)"; }}
                            >
                                <Download size={16} strokeWidth={2.5} /> Scarica documento
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
